import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useIdleLogout — memantau aktivitas user; setelah `idleMs` tanpa interaksi
 * memanggil `onIdle` (mis. auto-logout). Memberi jendela peringatan
 * `warningMs` sebelum idle untuk memungkinkan modal konfirmasi.
 *
 * Fase 7 policy ATLAS: 15 menit idle, warning muncul 1 menit sebelum expire.
 */
export function useIdleLogout({
  idleMs = 15 * 60 * 1000,
  warningMs = 60 * 1000,
  onIdle,
  onWarning,
  enabled = true,
} = {}) {
  const [remainingWarnMs, setRemainingWarnMs] = useState(null);
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const tickRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    idleTimerRef.current = null;
    warnTimerRef.current = null;
    tickRef.current = null;
  }, []);

  const start = useCallback(() => {
    cleanup();
    lastActivityRef.current = Date.now();
    warnedRef.current = false;
    setRemainingWarnMs(null);
    warnTimerRef.current = setTimeout(() => {
      warnedRef.current = true;
      setRemainingWarnMs(warningMs);
      onWarning && onWarning(warningMs);
      tickRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remain = idleMs - elapsed;
        if (remain <= 0) {
          setRemainingWarnMs(0);
        } else {
          setRemainingWarnMs(remain);
        }
      }, 1000);
    }, Math.max(0, idleMs - warningMs));
    idleTimerRef.current = setTimeout(() => {
      cleanup();
      setRemainingWarnMs(null);
      onIdle && onIdle();
    }, idleMs);
  }, [cleanup, idleMs, warningMs, onIdle, onWarning]);

  const reset = useCallback(() => {
    if (!enabled) return;
    start();
  }, [enabled, start]);

  useEffect(() => {
    if (!enabled) {
      cleanup();
      setRemainingWarnMs(null);
      return;
    }
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "scroll", "visibilitychange"];
    const handler = () => {
      // Kalau warning sedang aktif, biarkan modal yang memutuskan (butuh klik "Lanjutkan Sesi").
      if (warnedRef.current) return;
      reset();
    };
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    start();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      cleanup();
    };
  }, [enabled, start, reset, cleanup]);

  return { remainingWarnMs, extendSession: reset };
}
